import { AIConfiguration } from "../Bots/types";
import {
  WorkflowNode,
  NodeOption,
  triggerDefs,
  actionDefs,
  NodeColors,
  NodeId
} from "./types";

export function getNodeOptions(node: WorkflowNode) {
  if (node.type !== "action") {
    return null;
  }

  switch (node.actionType) {
    case "askQuestion":
      {
        if (node.questionType === "multipleChoice") {
          return [
            ...(node.options || []),
            {
              label: "Invalid reply",
              childNodeId: node.invalidChild
            }
          ] as NodeOption[];
        } else if (node.invalidReplyAction === "failBranch") {
          return [
            { label: "Valid reply", childNodeId: node.child },
            { label: "Invalid reply", childNodeId: node.invalidChild }
          ] as NodeOption[];
        }
      }
      break;

    case "condition": {
      const options = (node.conditionOptions || []).map((opt, i) => ({
        ...opt,
        label: opt.label || `Branch ${i + 1}`
      })) as NodeOption[];
      options.push({ label: "Else", childNodeId: node.fallbackChildId });
      return options;
    }

    case "httpRequest": {
      // Only render the two-branch view when an error branch has been enabled in the editor.
      // errorChildId === undefined means the toggle is off (single-connector, old behaviour).
      // errorChildId === null means enabled but not yet connected to a node.
      // errorChildId === number means connected to a specific node.
      if (node.errorChildId !== undefined) {
        return [
          { label: "On success", childNodeId: node.child },
          { label: "On error", childNodeId: node.errorChildId ?? undefined }
        ] as NodeOption[];
      }
      return null;
    }
  }

  return null;
}

//index is based on the array returned by getNodeOptions
export function updateNodeOption(
  index: number,
  childId: NodeId | undefined,
  node: WorkflowNode
) {
  if (node.type !== "action") {
    return null;
  }

  switch (node.actionType) {
    case "askQuestion":
      {
        if (node.questionType === "multipleChoice") {
          const options = node.options || [];
          if (index < options.length && options[index]) {
            // Regular option - check if option exists
            options[index].childNodeId = childId;
          } else if (index === options.length) {
            // Invalid reply branch
            node.invalidChild = childId;
          }
        } else if (node.invalidReplyAction === "failBranch") {
          if (index === 0) {
            node.child = childId;
          } else {
            node.invalidChild = childId;
          }
        }
      }
      break;

    case "condition": {
      const options = node.conditionOptions || [];
      if (index === options.length) {
        node.fallbackChildId = childId; //else branch
      } else if (index < options.length && options[index]) {
        // Regular branch - check if option exists before updating
        options[index].childNodeId = childId;
      }
      break;
    }

    case "httpRequest": {
      if (index === 0) {
        node.child = childId; // On success
      } else {
        node.errorChildId = childId; // On error
      }
      break;
    }
  }

  return null;
}

export function getNodeTitle(node: WorkflowNode) {
  if (node.type === "trigger") {
    // Handle legacy typo in saved workflows
    const trigger = node.trigger === ("vistior_left" as string) ? "visitor_left" : node.trigger;
    return triggerDefs[trigger as keyof typeof triggerDefs] ?? trigger;
  } else {
    return actionDefs[node.actionType]?.name ?? node.actionType;
  }
}

export function getNodeText(
  node: WorkflowNode,
  nodes: Record<NodeId, WorkflowNode>,
  aiConfigurations: AIConfiguration[],
  getDisplayNameForUser: (userId: number) => string,
  getTeamNameById: (teamId: number) => string
) {
  switch (node.type) {
    case "trigger": {
      // Show trigger-specific config summary on the canvas card
      const cfg = node.triggerConfig;
      if (!cfg) return "Start";
      switch (node.trigger) {
        case "csat_submitted": {
          if (cfg.csatMinRating || cfg.csatMaxRating) {
            const min = cfg.csatMinRating ?? 1;
            const max = cfg.csatMaxRating ?? 5;
            return `Rating ${min}–${max} stars`;
          }
          return "Any rating";
        }
        case "sla_breach":
          return cfg.slaThresholdMinutes ? `After ${cfg.slaThresholdMinutes} min` : "Start";
        case "scheduled":
          return cfg.scheduleExpression ? `Cron: ${cfg.scheduleExpression}` : "No schedule set";
        case "tier_escalated":
          return cfg.tierNumber ? `Tier ${cfg.tierNumber}` : "Any tier";
        case "email_opened":
        case "email_link_clicked":
          return cfg.broadcastId ? `Campaign #${cfg.broadcastId}` : "Any campaign";
        case "deal_stage_changed":
          return cfg.dealStage ? `Stage: ${cfg.dealStage}` : "Any stage";
        case "order_status_changed":
          return cfg.orderStatus ? `Status: ${cfg.orderStatus}` : "Any status";
        default:
          return "Start";
      }
    }
    case "action": {
      switch (node.actionType) {
        case "jumpTo": {
          if (!node.jumpToNodeId) {
            return "*No node chosen*";
          }

          const landingNode = nodes[node.jumpToNodeId];
          return landingNode?.name || "unnamed node";
        }
        case "sendMessage":
          return node.text;
        case "askQuestion":
          return node.question;
        case "closeConversation":
          return "Close conversation";
        case "httpRequest":
        case "condition":
          return node.description;
        case "transferToAI": {
          if (!node.aiConfigId) {
            return "*No AI Configuration chosen*";
          }
          const ai = Array.isArray(aiConfigurations)
            ? aiConfigurations.find((ai) => ai.id === node.aiConfigId)
            : undefined;
          return ai?.name || "*AI Configuration missing*";
        }
        case "netsuiteAI": {
          if (!node.aiConfigId) return "*No AI Configuration chosen*";
          const ai = Array.isArray(aiConfigurations)
            ? aiConfigurations.find((ai) => ai.id === node.aiConfigId)
            : undefined;
          const aiLabel = ai?.name || "*AI config missing*";
          return node.netSuiteConfigId
            ? `NetSuite AI — ${aiLabel}`
            : `NetSuite AI — *No NetSuite config chosen*`;
        }
        case "workflowSection": {
          return node.sectionName || node.name || "Unnamed section";
        }
        case "bookAppointment": {
          const emailVar = node.bookApptEmailVar || "visitorEmail";
          const duration = node.bookApptDurationOverride ? `${node.bookApptDurationOverride} min` : "default duration";
          return `Book Outlook Calendar event (${duration}) → save to <<${node.bookApptResultVar || "bookingConfirmation"}>>`;
        }
        case "sendCalendlyLink": {
          const resultVar = node.calendlyLinkResultVar || "bookingUrl";
          return node.calendlyLinkSendInChat !== false
            ? `Send Calendly link → <<${resultVar}>>`
            : `Generate Calendly link → <<${resultVar}>> (not auto-sent)`;
        }
        case "transferToAgents": {
          const agentName = getDisplayNameForUser(node.agentId || 0);
          const teamName = getTeamNameById(node.teamId || 0);
          if (node.specificTeam) {
            if (node.specificAgent)
              return `Transfer to ${agentName} on ${teamName}`;
            else return `Transfer to Agents on ${teamName}`;
          } else {
            return "Transfer to Agents on the current Team";
          }
        }
        case "setData": {
          const dataToSet = node.dataToSet;
          if (dataToSet?.length) {
            const firstData = dataToSet[0];
            const text = `Set ${firstData.name} to ${firstData.value}`;
            if (dataToSet.length > 1) {
              return text + " ... and more";
            }
            return text;
          } else {
            return "No data currently set";
          }
        }
        default:
          return "";
      }
    }
  }
}

export function getNodeColors(node: WorkflowNode): NodeColors {
  switch (node.type) {
    case "trigger":
      return {
        bgColor: "bg-blue-500",
        borderColor: "border-blue-500",
        optionColor: "bg-blue-500"
      };

    case "action":
      switch (node.actionType) {
        case "condition":
          return {
            bgColor: "bg-green-500",
            borderColor: "border-green-500",
            optionColor: "bg-green-500"
          };
        case "workflowSection":
          return {
            bgColor: "bg-amber-500",
            borderColor: "border-amber-500",
            optionColor: "bg-amber-500"
          };
        default:
          return {
            bgColor: "bg-grape-500",
            borderColor: "border-grape-500",
            optionColor: "bg-grape-500"
          };
      }
  }
}
